import { useState, useEffect } from 'react';
import { X, Save, AlertCircle, Zap, Calendar, FormInput, Users, DollarSign, Clock, Mail, Phone, Send, Check } from 'lucide-react';
import { Node } from 'reactflow';

interface TriggerConfigModalProps {
  node: Node;
  isOpen: boolean;
  onClose: () => void;
  onSave: (nodeId: string, config: any) => void;
}

const TRIGGER_TYPES = [
  {
    value: 'bulk_segment',
    label: 'Bulk Send to Customers',
    icon: Send,
    description: 'Send to all customers matching filters (with email/SMS validation)',
    color: 'emerald',
  },
  {
    value: 'membership_expired',
    label: 'Membership Expired',
    icon: DollarSign,
    description: 'Triggered when a membership plan expires',
    color: 'orange',
  },
  {
    value: 'membership_expiring_soon',
    label: 'Membership Expiring Soon',
    icon: Clock,
    description: 'Triggered X days before membership expires',
    color: 'amber',
  },
  {
    value: 'form_submitted',
    label: 'Form Submitted',
    icon: FormInput,
    description: 'Triggered when a specific form is submitted',
    color: 'blue',
  },
  {
    value: 'segment_joined',
    label: 'Joined Segment',
    icon: Users,
    description: 'Triggered when a contact joins a specific segment',
    color: 'purple',
  },
  {
    value: 'date_based',
    label: 'Date-Based',
    icon: Calendar,
    description: 'Triggered on specific dates (birthdays, anniversaries, etc.)',
    color: 'green',
  },
  {
    value: 'tag_added',
    label: 'Tag Added',
    icon: Zap,
    description: 'Triggered when a specific tag is added to a contact',
    color: 'indigo',
  },
  {
    value: 'field_changed',
    label: 'Field Changed',
    icon: Zap,
    description: 'Triggered when a specific field value changes',
    color: 'pink',
  },
  {
    value: 'manual',
    label: 'Manual',
    icon: Zap,
    description: 'Manually triggered by user action',
    color: 'gray',
  },
];

const DATE_FIELD_TYPES = [
  { value: 'birthday', label: 'Birthday' },
  { value: 'anniversary', label: 'Anniversary' },
  { value: 'custom_date', label: 'Custom Date Field' },
  { value: 'membership_start', label: 'Membership Start Date' },
  { value: 'last_service', label: 'Last Service Date' },
];

export function TriggerConfigModal({ node, isOpen, onClose, onSave }: TriggerConfigModalProps) {
  const [config, setConfig] = useState<any>(node.data.config || {});
  const [triggerName, setTriggerName] = useState(node.data.label || '');
  const [errors, setErrors] = useState<string[]>([]);
  const [selectedType, setSelectedType] = useState(config.trigger_type || 'membership_expired');

  useEffect(() => {
    setConfig(node.data.config || {});
    setTriggerName(node.data.label || '');
    setSelectedType(node.data.config?.trigger_type || 'membership_expired');
    setErrors([]);
  }, [node]);

  if (!isOpen) return null;

  const validateConfig = (): boolean => {
    const newErrors: string[] = [];

    if (!triggerName.trim()) {
      newErrors.push('Trigger name is required');
    }

    if (!selectedType) {
      newErrors.push('Trigger type is required');
    }

    // Type-specific validation
    switch (selectedType) {
      case 'membership_expiring_soon':
        if (!config.days_before || config.days_before < 1) {
          newErrors.push('Days before expiration must be greater than 0');
        }
        break;
      case 'form_submitted':
        if (!config.form_id) {
          newErrors.push('Form selection is required');
        }
        break;
      case 'segment_joined':
        if (!config.segment_id) {
          newErrors.push('Segment selection is required');
        }
        break;
      case 'date_based':
        if (!config.date_field) {
          newErrors.push('Date field is required');
        }
        break;
      case 'tag_added':
        if (!config.tag_name) {
          newErrors.push('Tag name is required');
        }
        break;
      case 'field_changed':
        if (!config.field_name) {
          newErrors.push('Field name is required');
        }
        break;
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSave = () => {
    if (validateConfig()) {
      onSave(node.id, {
        label: triggerName,
        config: {
          ...config,
          trigger_type: selectedType,
        },
      });
      onClose();
    }
  };

  const handleTypeChange = (type: string) => {
    setSelectedType(type);
    setConfig({
      trigger_type: type,
    });
  };

  const renderTypeSpecificConfig = () => {
    switch (selectedType) {
      case 'membership_expiring_soon':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Days Before Expiration *
              </label>
              <input
                type="number"
                min="1"
                max="90"
                value={config.days_before || 7}
                onChange={(e) => setConfig({ ...config, days_before: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="mt-1 text-xs text-gray-500">
                Workflow will trigger this many days before membership expires
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Membership Type (Optional)
              </label>
              <select
                value={config.membership_type || 'all'}
                onChange={(e) => setConfig({ ...config, membership_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Membership Types</option>
                <option value="comfort_club">Comfort Club</option>
                <option value="premium">Premium</option>
                <option value="basic">Basic</option>
              </select>
            </div>
          </div>
        );

      case 'form_submitted':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Form *
              </label>
              <select
                value={config.form_id || ''}
                onChange={(e) => setConfig({ ...config, form_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select a form...</option>
                <option value="contact_form">Contact Form</option>
                <option value="quote_request">Quote Request</option>
                <option value="service_booking">Service Booking</option>
                <option value="newsletter_signup">Newsletter Signup</option>
              </select>
            </div>
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={config.trigger_once_per_contact || false}
                  onChange={(e) => setConfig({ ...config, trigger_once_per_contact: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Only trigger once per contact (prevent duplicate enrollments)
                </span>
              </label>
            </div>
          </div>
        );

      case 'segment_joined':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Segment *
              </label>
              <select
                value={config.segment_id || ''}
                onChange={(e) => setConfig({ ...config, segment_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select a segment...</option>
                <option value="high_value">High Value Customers</option>
                <option value="inactive">Inactive Customers</option>
                <option value="new_leads">New Leads</option>
                <option value="past_due">Past Due</option>
              </select>
            </div>
          </div>
        );

      case 'date_based':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date Field *
              </label>
              <select
                value={config.date_field || ''}
                onChange={(e) => setConfig({ ...config, date_field: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select date field...</option>
                {DATE_FIELD_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Trigger Timing
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <input
                    type="number"
                    min="-365"
                    max="365"
                    value={config.days_offset || 0}
                    onChange={(e) => setConfig({ ...config, days_offset: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <select
                    value={config.offset_type || 'on'}
                    onChange={(e) => setConfig({ ...config, offset_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="before">days before</option>
                    <option value="on">on the date</option>
                    <option value="after">days after</option>
                  </select>
                </div>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Example: "7 days before birthday" or "1 day after anniversary"
              </p>
            </div>
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={config.repeat_annually || true}
                  onChange={(e) => setConfig({ ...config, repeat_annually: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Repeat annually</span>
              </label>
            </div>
          </div>
        );

      case 'tag_added':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tag Name *
              </label>
              <input
                type="text"
                value={config.tag_name || ''}
                onChange={(e) => setConfig({ ...config, tag_name: e.target.value })}
                placeholder="e.g., vip, lead, qualified"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        );

      case 'field_changed':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Field Name *
              </label>
              <select
                value={config.field_name || ''}
                onChange={(e) => setConfig({ ...config, field_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select field...</option>
                <option value="status">Status</option>
                <option value="membership_tier">Membership Tier</option>
                <option value="service_plan">Service Plan</option>
                <option value="payment_status">Payment Status</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Changed To (Optional)
              </label>
              <input
                type="text"
                value={config.changed_to_value || ''}
                onChange={(e) => setConfig({ ...config, changed_to_value: e.target.value })}
                placeholder="Leave empty to trigger on any change"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="mt-1 text-xs text-gray-500">
                Specify a value to only trigger when field changes to this specific value
              </p>
            </div>
          </div>
        );

      case 'bulk_segment':
        return (
          <div className="space-y-6">
            {/* Recipient Filters */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Recipient Filters</h4>
              <div className="space-y-3 bg-gray-50 rounded-lg p-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={config.segmentFilters?.requireValidEmail ?? false}
                    onChange={(e) => setConfig({
                      ...config,
                      segmentFilters: { ...config.segmentFilters, requireValidEmail: e.target.checked }
                    })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    <Mail className="w-4 h-4 inline mr-1" />
                    Only send emails to validated email addresses
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={config.segmentFilters?.requireVerifiedSms ?? false}
                    onChange={(e) => setConfig({
                      ...config,
                      segmentFilters: { ...config.segmentFilters, requireVerifiedSms: e.target.checked }
                    })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    <Phone className="w-4 h-4 inline mr-1" />
                    Only send SMS to verified phone numbers
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={config.segmentFilters?.excludeDoNotContact ?? true}
                    onChange={(e) => setConfig({
                      ...config,
                      segmentFilters: { ...config.segmentFilters, excludeDoNotContact: e.target.checked }
                    })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Exclude customers on do-not-contact list
                  </span>
                </label>
              </div>
            </div>

            {/* Agreement Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Agreement Status (Optional)
              </label>
              <select
                multiple
                value={config.segmentFilters?.agreementStatus || []}
                onChange={(e) => {
                  const values = Array.from(e.target.selectedOptions, option => option.value);
                  setConfig({
                    ...config,
                    segmentFilters: { ...config.segmentFilters, agreementStatus: values }
                  });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                size={4}
              >
                <option value="expired">Expired Membership</option>
                <option value="none">No Membership</option>
                <option value="active">Active Membership</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Hold Ctrl/Cmd to select multiple. Leave empty for all customers.
              </p>
            </div>

            {/* Inactivity Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum Days Since Last Service (Optional)
              </label>
              <input
                type="number"
                min="0"
                value={config.segmentFilters?.inactiveDaysMin || ''}
                onChange={(e) => setConfig({
                  ...config,
                  segmentFilters: { ...config.segmentFilters, inactiveDaysMin: parseInt(e.target.value) || undefined }
                })}
                placeholder="e.g., 365 for 1 year inactive"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        );

      case 'manual':
        return (
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              This workflow will only be triggered manually by selecting contacts and choosing "Enroll in Workflow" from the actions menu.
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  const renderSenderSettings = () => (
    <div className="border-t pt-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Mail className="w-5 h-5" />
        Sender Settings (Optional)
      </h3>
      <p className="text-sm text-gray-500 mb-4">
        Override the default sender for all emails and SMS in this workflow
      </p>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            From Email Address
          </label>
          <input
            type="email"
            value={config.fromEmail || ''}
            onChange={(e) => setConfig({ ...config, fromEmail: e.target.value })}
            placeholder="e.g., george@my-ccs.com"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            From Name
          </label>
          <input
            type="text"
            value={config.fromName || ''}
            onChange={(e) => setConfig({ ...config, fromName: e.target.value })}
            placeholder="e.g., George McPherson"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Reply-To Email
          </label>
          <input
            type="email"
            value={config.replyTo || ''}
            onChange={(e) => setConfig({ ...config, replyTo: e.target.value })}
            placeholder="Same as From Email if blank"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            From Phone Number (SMS)
          </label>
          <input
            type="tel"
            value={config.fromPhone || ''}
            onChange={(e) => setConfig({ ...config, fromPhone: e.target.value })}
            placeholder="e.g., +15616804467"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="mt-1 text-xs text-gray-500">
            Must be a verified Twilio number in E.164 format
          </p>
        </div>
      </div>
    </div>
  );

  const selectedTriggerInfo = TRIGGER_TYPES.find(t => t.value === selectedType);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Configure Workflow Trigger</h2>
            <p className="text-sm text-gray-500 mt-1">Define when this workflow should start</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Error Display */}
        {errors.length > 0 && (
          <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-red-800 mb-1">
                  Please fix the following errors:
                </h3>
                <ul className="text-sm text-red-700 space-y-1">
                  {errors.map((error, idx) => (
                    <li key={idx}>• {error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="space-y-6">
            {/* Trigger Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Trigger Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={triggerName}
                onChange={(e) => setTriggerName(e.target.value)}
                placeholder="e.g., Comfort Club Expiration"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Trigger Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Trigger Type <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                {TRIGGER_TYPES.map((type) => {
                  const Icon = type.icon;
                  const isSelected = selectedType === type.value;

                  // Explicit color classes for Tailwind purge safety
                  const colorClasses: Record<string, { border: string; bg: string; iconBg: string; iconText: string; ring: string }> = {
                    emerald: { border: 'border-emerald-500', bg: 'bg-emerald-50', iconBg: 'bg-emerald-100', iconText: 'text-emerald-600', ring: 'ring-emerald-300' },
                    orange: { border: 'border-orange-500', bg: 'bg-orange-50', iconBg: 'bg-orange-100', iconText: 'text-orange-600', ring: 'ring-orange-300' },
                    blue: { border: 'border-blue-500', bg: 'bg-blue-50', iconBg: 'bg-blue-100', iconText: 'text-blue-600', ring: 'ring-blue-300' },
                    purple: { border: 'border-purple-500', bg: 'bg-purple-50', iconBg: 'bg-purple-100', iconText: 'text-purple-600', ring: 'ring-purple-300' },
                    teal: { border: 'border-teal-500', bg: 'bg-teal-50', iconBg: 'bg-teal-100', iconText: 'text-teal-600', ring: 'ring-teal-300' },
                    rose: { border: 'border-rose-500', bg: 'bg-rose-50', iconBg: 'bg-rose-100', iconText: 'text-rose-600', ring: 'ring-rose-300' },
                    cyan: { border: 'border-cyan-500', bg: 'bg-cyan-50', iconBg: 'bg-cyan-100', iconText: 'text-cyan-600', ring: 'ring-cyan-300' },
                    indigo: { border: 'border-indigo-500', bg: 'bg-indigo-50', iconBg: 'bg-indigo-100', iconText: 'text-indigo-600', ring: 'ring-indigo-300' },
                    pink: { border: 'border-pink-500', bg: 'bg-pink-50', iconBg: 'bg-pink-100', iconText: 'text-pink-600', ring: 'ring-pink-300' },
                    gray: { border: 'border-gray-500', bg: 'bg-gray-50', iconBg: 'bg-gray-100', iconText: 'text-gray-600', ring: 'ring-gray-300' },
                  };
                  const colors = colorClasses[type.color] || colorClasses.gray;

                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => handleTypeChange(type.value)}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        isSelected
                          ? `${colors.border} ${colors.bg} ring-2 ring-offset-1 ${colors.ring}`
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${colors.iconBg}`}>
                          <Icon className={`w-5 h-5 ${colors.iconText}`} />
                        </div>
                        <div className="flex-1">
                          <div className={`font-medium ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>{type.label}</div>
                          <div className="text-xs text-gray-500 mt-1">{type.description}</div>
                          {isSelected && (
                            <div className="mt-2 text-xs font-medium text-green-600 flex items-center gap-1">
                              <Check className="w-3 h-3" /> Selected
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Type-Specific Configuration */}
            {selectedTriggerInfo && (
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {selectedTriggerInfo.label} Settings
                </h3>
                {renderTypeSpecificConfig()}
              </div>
            )}

            {/* Sender Settings */}
            {renderSenderSettings()}

            {/* Enrollment Limits */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Enrollment Settings</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Enrollments Per Contact
                  </label>
                  <select
                    value={config.max_enrollments || 'unlimited'}
                    onChange={(e) => setConfig({ ...config, max_enrollments: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="unlimited">Unlimited</option>
                    <option value="1">Once per contact</option>
                    <option value="1_per_year">Once per year</option>
                    <option value="1_per_month">Once per month</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Limit how many times a contact can be enrolled in this workflow
                  </p>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={config.allow_re_enrollment || false}
                      onChange={(e) => setConfig({ ...config, allow_re_enrollment: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Allow re-enrollment after workflow completion
                    </span>
                  </label>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={config.suppress_if_in_workflow || true}
                      onChange={(e) => setConfig({ ...config, suppress_if_in_workflow: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Don't enroll if already in another workflow
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            Save Trigger
          </button>
        </div>
      </div>
    </div>
  );
}
